import React from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import {
    Users,
    Truck,
    CheckCircle2,
    PhoneOff,
    Undo2,
    Ban,
    Package,
    MessageSquare,
    PhoneCall,
    Phone,
    Percent,
    PackageCheck,
    PackageX,
} from 'lucide-react';

import { dashboardApi } from '../lib/dashboardApi';
import { ordersApi } from '../lib/ordersApi';
import DashboardFilters from '../components/dashboard/DashboardFilters';
import KpiCard from '../components/dashboard/KpiCard';
import KpiCardSkeleton from '../components/dashboard/KpiCardSkeleton';
import PerformanceTable from '../components/dashboard/PerformanceTable';
import DailyChart from '../components/dashboard/DailyChart';
import StatusDonutChart from '../components/dashboard/StatusDonutChart';

/**
 * Dashboard — Phase 5A.
 *
 * Layout:
 *   1. Filter bar (period pills + 6 dropdowns).
 *   2. Row 1: 6 KPI cards — contact flow (Total Leads, Call 1/Msg 1,
 *             Call 2/Msg 2, Call 3, Sans Réponse, Faux).
 *   3. Row 2: 7 KPI cards — final results (Confirmé, In Delivery, Retourné,
 *             Quantité Totale, Taux Conf., Taux Livr., Taux Retour).
 *   4. Performance table (Call Center × Product) — 60% width on xl screens.
 *
 * The page is only reachable by super_admin / manager (routeConfig +
 * backend 403). This component doesn't need its own guard, AppLayout does.
 */
const DEFAULT_FILTERS = { period: 'this_month' };

// Map donut segment status → the `statut` URL param expected by CommandesPage.
// "pending" is the synthetic segment on the donut and expands to the 3 real
// call/message trackings, which /api/orders now accepts comma-separated.
const PENDING_TRIPLE = 'call_1_message_1,call_2_message_2,call_3';
function statusParamForSegment(seg) {
    return seg.status === 'pending' ? PENDING_TRIPLE : seg.status;
}

export default function DashboardPage() {
    const { user } = useOutletContext();
    const navigate = useNavigate();
    const isAgent = user?.role === 'cc_agent';
    const [filters, setFilters] = React.useState(DEFAULT_FILTERS);
    const [data, setData] = React.useState(null);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState(null);
    const [filterValues, setFilterValues] = React.useState(null);

    // Phase 5B — chart states.
    const [granularity, setGranularity] = React.useState('day');
    const [daily, setDaily] = React.useState(null);
    const [dailyLoading, setDailyLoading] = React.useState(false);
    const [dailyError, setDailyError] = React.useState(null);
    const [donut, setDonut] = React.useState(null);
    const [donutLoading, setDonutLoading] = React.useState(false);
    const [donutError, setDonutError] = React.useState(null);

    React.useEffect(() => {
        // Agents also need `products` (their scoped list) to power the Product
        // dropdown in the agent filter bar. `/api/orders/filters` is
        // server-scoped for cc_agent, so no CC leak here.
        ordersApi.filters().then(setFilterValues).catch(() => setFilterValues(null));
    }, []);

    const shouldSkipFetch = filters.period === 'custom' && (!filters.date_from || !filters.date_to);

    const fetchKpis = React.useCallback(async () => {
        // Skip the request while the user is picking a custom range.
        // Without both date_from and date_to the backend returns 400 which
        // would flash a red banner between clicking "Personnalisé" and
        // clicking "Appliquer".
        if (shouldSkipFetch) return;
        setLoading(true);
        setError(null);
        try {
            const res = await dashboardApi.kpis(filters);
            setData(res);
        } catch (e) {
            setError(e.detail || e.message);
            setData(null);
        } finally {
            setLoading(false);
        }
    }, [filters, shouldSkipFetch]);

    const fetchDaily = React.useCallback(async () => {
        if (shouldSkipFetch) return;
        setDailyLoading(true);
        setDailyError(null);
        try {
            const res = await dashboardApi.chartDaily({ ...filters, granularity });
            setDaily(res);
        } catch (e) {
            setDailyError(e.detail || e.message);
            setDaily(null);
        } finally {
            setDailyLoading(false);
        }
    }, [filters, granularity, shouldSkipFetch]);

    const fetchDonut = React.useCallback(async () => {
        if (shouldSkipFetch) return;
        setDonutLoading(true);
        setDonutError(null);
        try {
            const res = await dashboardApi.chartStatus(filters);
            setDonut(res);
        } catch (e) {
            setDonutError(e.detail || e.message);
            setDonut(null);
        } finally {
            setDonutLoading(false);
        }
    }, [filters, shouldSkipFetch]);

    React.useEffect(() => { fetchKpis(); }, [fetchKpis]);
    React.useEffect(() => { fetchDaily(); }, [fetchDaily]);
    React.useEffect(() => { fetchDonut(); }, [fetchDonut]);

    // Donut deep-link → /commandes?statut=<...>&date_from=<...>&date_to=<...>
    // Uses the exact ISO range the backend computed for the CURRENT period
    // (so it stays consistent across "today" / "this_week" / custom, and
    // survives DST/timezone edge cases). Only status + period are transferred
    // (client decision — CC/city/etc filters are NOT propagated).
    const handleDonutClick = React.useCallback((segment) => {
        if (!segment || !segment.count) return;
        const cur = data?.period?.current;
        if (!cur) return;
        const params = new URLSearchParams({
            statut: statusParamForSegment(segment),
            date_from: cur.start,
            date_to: cur.end,
        });
        navigate(`/commandes?${params.toString()}`);
    }, [data, navigate]);

    // "Voir le rapport →" — same period range as the donut drill-down but
    // WITHOUT a status filter (Phase 12.4). cc_agent scoping is enforced
    // server-side on /commandes, so no extra work needed for agents.
    const handleReportClick = React.useCallback(() => {
        const cur = data?.period?.current;
        if (!cur) return;
        const params = new URLSearchParams({
            date_from: cur.start,
            date_to: cur.end,
        });
        navigate(`/commandes?${params.toString()}`);
    }, [data, navigate]);

    const cur = data?.kpis?.current || {};
    const prev = data?.kpis?.previous || {};
    const perfGroups = data?.performance_grouped || [];

    return (
        <div data-testid="dashboard-page" data-role={user?.role} className="space-y-4">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <h2 className="text-2xl font-bold text-[#1A1D2E]" data-testid="dashboard-title">
                        {isAgent ? 'Mon Dashboard' : 'Dashboard'}
                    </h2>
                    <span className="text-sm text-[#6B7280]" data-testid="dashboard-subtitle">
                        {isAgent
                            ? `${user?.display_name || ''}${user?.call_center_name ? ` — ${user.call_center_name}` : ''}`
                            : "Vue d'ensemble des performances"}
                    </span>
                </div>
                <span className="text-sm text-[#6B7280]" data-testid="dashboard-user-hint">
                    Bienvenue, {user?.display_name}
                </span>
            </div>

            <DashboardFilters filters={filters} onChange={setFilters} filterValues={filterValues} mode={isAgent ? 'agent' : 'full'} />

            {error && (
                <div data-testid="dashboard-error" className="bg-white border border-[#EF4444]/30 rounded-xl px-4 py-3 text-sm text-[#EF4444]">
                    {typeof error === 'string' ? error : 'Erreur lors du chargement des KPI.'}
                </div>
            )}

            {/* KPI skeletons — shown ONLY on the very first load (no cached data). */}
            {loading && !data && !error && (
                <div className="space-y-3" data-testid="kpi-skeletons">
                    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
                        {Array.from({ length: 6 }).map((_, i) => <KpiCardSkeleton key={i} />)}
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-7 gap-3">
                        {Array.from({ length: 7 }).map((_, i) => <KpiCardSkeleton key={i} />)}
                    </div>
                </div>
            )}

            {/* Row 1: 6 cards — contact flow */}
            {data && (
            <div data-testid="kpi-row-1" className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
                <KpiCard testId="kpi-total-leads"     label="Total Leads"        Icon={Users}         iconColor="#4B3FD8" value={cur.total_leads}      previous={prev.total_leads} />
                <KpiCard testId="kpi-call-1"          label="Call 1 / Message 1" Icon={MessageSquare} iconColor="#F59E0B" value={cur.call_1_message_1} previous={prev.call_1_message_1} />
                <KpiCard testId="kpi-call-2"          label="Call 2 / Message 2" Icon={PhoneCall}     iconColor="#F97316" value={cur.call_2_message_2} previous={prev.call_2_message_2} />
                <KpiCard testId="kpi-call-3"          label="Call 3"             Icon={Phone}         iconColor="#EA580C" value={cur.call_3}           previous={prev.call_3} />
                <KpiCard testId="kpi-no-reply"        label="Sans Réponse"       Icon={PhoneOff}      iconColor="#EF4444" value={cur.no_reply}         previous={prev.no_reply}         inversedDelta />
                <KpiCard testId="kpi-fake"            label="Faux"               Icon={Ban}           iconColor="#4B5563" value={cur.fake}             previous={prev.fake}             inversedDelta />
            </div>
            )}

            {/* Row 2: 7 cards — final results */}
            {data && (
            <div data-testid="kpi-row-2" className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-7 gap-3">
                <KpiCard testId="kpi-confirmed"         label="Confirmé"             Icon={CheckCircle2}  iconColor="#10B981" value={cur.confirmed}         previous={prev.confirmed} />
                <KpiCard testId="kpi-in-delivery"       label="In Delivery"          Icon={Truck}         iconColor="#0D9488" value={cur.in_delivery}       previous={prev.in_delivery} />
                <KpiCard testId="kpi-returned"          label="Retourné"             Icon={Undo2}         iconColor="#DC2626" value={cur.returned}          previous={prev.returned}      inversedDelta />
                <KpiCard testId="kpi-quantity-total"    label="Quantité Totale"      Icon={Package}       iconColor="#4B3FD8" value={cur.quantity_total}    previous={prev.quantity_total} />
                <KpiCard testId="kpi-confirmation-rate" label="Taux de Confirmation" Icon={Percent}       iconColor="#10B981" value={cur.confirmation_rate} previous={prev.confirmation_rate} format="percent" />
                <KpiCard testId="kpi-delivery-rate"     label="Taux de Livraison"    Icon={PackageCheck}  iconColor="#0D9488" value={cur.delivery_rate}     previous={prev.delivery_rate}     format="percent" />
                <KpiCard testId="kpi-return-rate"       label="Taux de Retour"       Icon={PackageX}      iconColor="#DC2626" value={cur.return_rate}       previous={prev.return_rate}       format="percent" inversedDelta />
            </div>
            )}

            {/* Performance section — SUPER ADMIN / MANAGER ONLY.
                Grouped CC table (60%) + Status donut (40%) side by side.
                cc_agent has its own reordered layout further below. */}
            {!isAgent && (
                <div className="grid grid-cols-1 xl:grid-cols-5 gap-3">
                    <div className="xl:col-span-3">
                        <PerformanceTable groups={perfGroups} loading={loading && !data} error={null} />
                    </div>
                    <div className="xl:col-span-2">
                        <StatusDonutChart
                            data={donut}
                            loading={donutLoading && !donut}
                            error={donutError}
                            onSegmentClick={handleDonutClick}
                            onReportClick={handleReportClick}
                        />
                    </div>
                </div>
            )}

            {/* Daily line chart — SUPER ADMIN / MANAGER ONLY (full width, bottom). */}
            {!isAgent && (
                <DailyChart
                    data={daily}
                    granularity={granularity}
                    onGranularityChange={setGranularity}
                    loading={dailyLoading && !daily}
                    error={dailyError}
                />
            )}

            {/* Agent CC layout (Phase 12.5 reorder, Phase 14.1 simplified):
                (1) KPI cards above (already rendered)
                (2) Donut + Daily chart side by side, 50/50, equal height.
                (Phase 14.1: the "Commandes à rappeler" section was removed
                 at the client's request.) */}
            {isAgent && (
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-3 items-stretch">
                    <StatusDonutChart
                        data={donut}
                        loading={donutLoading && !donut}
                        error={donutError}
                        onSegmentClick={handleDonutClick}
                        onReportClick={handleReportClick}
                    />
                    <DailyChart
                        data={daily}
                        granularity={granularity}
                        onGranularityChange={setGranularity}
                        loading={dailyLoading && !daily}
                        error={dailyError}
                    />
                </div>
            )}
        </div>
    );
}
